import React, { useContext, useState } from 'react';
import {
  MenuOptionGroup,
  MenuOptionGroupProps,
  MenuItemOption,
  Box,
  forwardRef,
} from '@chakra-ui/react';
import { ActionMenuContext } from './action-menu-context';
import { getActionMenuOptionGroupStyles } from './action-menu-option-group.theme';
import { ActionListItemOption } from './action-list-item-option';
import {
  getActionListItemOptionEmptyCheckboxStyles,
  getActionListItemOptionStyles,
  getActionListOptionIconStyles,
} from './action-list-item-option.theme';
import {
  getActionListItemTextStyle,
  getActionListItemDescriptionStyles,
} from './action-list-item.theme';
import { CheckOutlineIcon } from '@allergan-data-labs/alle-elements-icons/24/checkOutlineIcon';
import { CheckboxEmptyIcon } from '@allergan-data-labs/alle-elements-icons/24/checkboxEmptyIcon';
import { CheckboxSelectedIcon } from '@allergan-data-labs/alle-elements-icons/24/checkboxSelectedIcon';
import { DESCRIPTION_CLASSNAME } from './action-list-item';

export interface ActionMenuOptionGroupProps extends MenuOptionGroupProps {
  children:
    | React.ReactElement<typeof ActionListItemOption>
    | React.ReactElement<typeof ActionListItemOption>[];
}

const isOptionItemSelected = (
  selectedValue: string | string[] | null,
  value: string
): boolean => {
  if (selectedValue === null) {
    return false;
  }

  if (Array.isArray(selectedValue)) {
    return selectedValue.includes(value);
  }

  return selectedValue === value;
};

/**
 * The `ActionMenuOptionGroup` component groups together a list of `ActionListItemOption`
 * components and specifies if the `ActionListItemOption` components will act
 * like a radio or checkbox type component.
 */
export const ActionMenuOptionGroup = forwardRef<
  ActionMenuOptionGroupProps,
  'div'
>(({ children: c, onChange, ...props }, _) => {
  const { colorMode } = useContext(ActionMenuContext);
  const [selectedValue, setSelectedValue] = useState<string | string[] | null>(
    props.defaultValue || null
  );
  const onChangeHandler = (value: string | string[]): void => {
    setSelectedValue(value);
    if (typeof onChange === 'function') {
      onChange(value);
    }
  };

  /**
   * Ensures that if only 1 child is provided it still renders properly even
   * though this scenario is an anti-pattern
   */
  if (!Array.isArray(c)) {
    c = [c];
  }

  return (
    <MenuOptionGroup
      onChange={onChangeHandler}
      {...props}
      sx={getActionMenuOptionGroupStyles(colorMode)}
    >
      {Array.isArray(c)
        ? c.map((child: React.ReactElement, index) => {
            const cprops = child.props;
            const { children, description, ...otherProps } = cprops;
            const icon =
              props.type === 'checkbox' ? (
                <CheckboxSelectedIcon
                  sx={getActionListOptionIconStyles(
                    colorMode,
                    cprops.isDisabled || false
                  )}
                />
              ) : (
                <CheckOutlineIcon
                  sx={getActionListOptionIconStyles(
                    colorMode,
                    cprops.isDisabled || false
                  )}
                />
              );
            return (
              <MenuItemOption
                icon={icon}
                key={index}
                sx={getActionListItemOptionStyles(colorMode)}
                {...otherProps}
              >
                {description ? (
                  <Box sx={getActionListItemTextStyle(colorMode, false)}>
                    {!isOptionItemSelected(selectedValue, cprops.value) &&
                      props.type === 'checkbox' && (
                        <Box sx={getActionListItemOptionEmptyCheckboxStyles()}>
                          <CheckboxEmptyIcon
                            sx={getActionListOptionIconStyles(
                              colorMode,
                              cprops.isDisabled || false
                            )}
                          />
                        </Box>
                      )}
                    {children}
                    <Box
                      className={DESCRIPTION_CLASSNAME}
                      sx={getActionListItemDescriptionStyles(colorMode)}
                    >
                      {description}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={getActionListItemTextStyle(colorMode, false)}>
                    {!isOptionItemSelected(selectedValue, cprops.value) &&
                      props.type === 'checkbox' && (
                        <Box sx={getActionListItemOptionEmptyCheckboxStyles()}>
                          <CheckboxEmptyIcon
                            sx={getActionListOptionIconStyles(
                              colorMode,
                              cprops.isDisabled || false
                            )}
                          />
                        </Box>
                      )}
                    {children}
                  </Box>
                )}
              </MenuItemOption>
            );
          })
        : ''}
    </MenuOptionGroup>
  );
});
